/**
 * Operate-UI client for the save-viz endpoint that backs the "Save as
 * Cognos report" affordance under each rendered chart. Mirrors the
 * shape of narrateApi — one-shot request/response, structured-error
 * surfacing so the modal can show actionable feedback inline.
 */
import { AxiosError } from "axios";
import { api } from "./api";

export interface SaveVizResult {
  success:    true;
  /** Cognos search-path of the newly created report, e.g. /content/folder[@name='X']/report[@name='Y']. */
  searchPath: string;
  /** Cognos store-ID of the newly created report. */
  storeId:    string;
  reportName: string;
  /**
   * Which tier the save landed in (Feature B §5.5):
   * NATIVE_LIVE (live query + native chart) / IMAGE_SNAPSHOT
   * (Feature A) / TABLE_SNAPSHOT. Empty on older backends.
   */
  tier:       string;
}

/**
 * Source kinds the backend's allow-list accepts as save provenance.
 * Mirrors SaveVizController.SOURCE_REF_KIND_ALLOW_LIST — anything
 * else is stripped client-side so a non-native-eligible chart still
 * saves as a plain snapshot instead of being rejected with 400.
 */
const SOURCE_REF_KIND_ALLOW_LIST = new Set([
  "cognos.package.query",
  "cognos.dataModule.run",
]);

export interface SaveVizApiError {
  /** Stable error code from the backend (saver.unavailable / saver.rejected / saver.failure / http.status). */
  code:   string;
  /** Human-readable diagnostic safe to render in the modal. */
  detail: string;
  status: number;
}

/**
 * Persist a viz dataset as a Cognos report snapshot. Calls
 * {@code POST /api/v1/visualize/save}; the endpoint is admin-only and
 * delegates to {@code CognosReportSaver}.
 *
 * <p>Throws {@link SaveVizApiError} for any non-2xx — the modal
 * differentiates "saver.unavailable" (offer a "contact admin" hint)
 * from "saver.rejected" (the user can fix it: rename, pick a different
 * folder, etc.).
 */
export async function saveVizAsCognosReport(
  reportName:        string,
  targetFolder:      string,
  columns:           string[],
  rows:              Array<Array<unknown>>,
  subtitle?:         string,
  chartImageDataUrl?: string,
  // B0 (save-viz Feature B): source provenance + rendered spec. Both
  // optional — when the sourceRef kind is allow-listed the backend
  // attempts a NATIVE_LIVE save and falls back to the snapshot
  // otherwise; rows keep being sent as the last-resort payload.
  sourceRef?:        { kind: string; params: Record<string, unknown> },
  vizSpec?:          { format: string; vizType: string; specJson: string },
): Promise<SaveVizResult> {
  const allowedSourceRef =
    sourceRef && SOURCE_REF_KIND_ALLOW_LIST.has(sourceRef.kind) ? sourceRef : undefined;
  try {
    const { data } = await api.post<{
      success?:    boolean;
      searchPath?: string;
      storeId?:    string;
      reportName?: string;
      tier?:       string;
    }>("/visualize/save", {
      reportName,
      targetFolder,
      columns,
      rows,
      subtitle: subtitle && subtitle.trim() !== "" ? subtitle : undefined,
      // data:image/png;base64,... captured client-side; the backend
      // embeds it above the data table. Omitted when capture yielded
      // nothing, in which case the report is table-only.
      chartImageDataUrl:
        chartImageDataUrl && chartImageDataUrl.trim() !== "" ? chartImageDataUrl : undefined,
      sourceRef: allowedSourceRef,
      vizSpec:   allowedSourceRef ? vizSpec : undefined,
    });
    return {
      success:    true,
      searchPath: typeof data?.searchPath === "string" ? data.searchPath : "",
      storeId:    typeof data?.storeId    === "string" ? data.storeId    : "",
      reportName: typeof data?.reportName === "string" ? data.reportName : reportName,
      tier:       typeof data?.tier       === "string" ? data.tier       : "",
    };
  } catch (e) {
    if (e instanceof AxiosError) {
      const body = e.response?.data as { error?: string; detail?: string } | undefined;
      const err: SaveVizApiError = {
        code:   typeof body?.error  === "string" ? body.error  : "http.status",
        detail: typeof body?.detail === "string" ? body.detail : (e.message ?? `HTTP ${e.response?.status}`),
        status: e.response?.status ?? 0,
      };
      throw err;
    }
    throw e;
  }
}
